import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useUpdateWarehouseShelving } from "../../hooks"
import type { WarehouseShelvingFormValues } from "../../schemas"
import { warehouseShelvingFormSchema } from "../../schemas"

import WarehouseShelvingForm from "./WarehouseShelvingForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { WarehouseShelving, WarehouseShelvingUpdateInput } from "@/graphql/codegen/graphql"

interface WarehouseShelvingFormDialogProps {
  shelving: WarehouseShelving
  onSubmit: () => void
  onClose: () => void
}

const WarehouseShelvingFormDialog: FC<WarehouseShelvingFormDialogProps> = ({ shelving, onSubmit, onClose }) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const methods = useForm<WarehouseShelvingFormValues>({
    defaultValues: {
      coldStorage: shelving.coldStorage,
      h3ColdStorage: shelving.h3ColdStorage,
      binSize: shelving.binSize ?? null,
      deep: shelving.deep,
      isDoNotUse: Boolean(shelving.isDoNotUse),
    },
    resolver: zodResolver(warehouseShelvingFormSchema),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
  } = methods
  const { update } = useUpdateWarehouseShelving(onSubmit)
  const canSubmit = isDirty && isValid && !isSubmitting

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const submitForm = async (formValues: WarehouseShelvingFormValues) => {
    await update({ warehouseShelvingId: shelving.warehouseShelvingId, ...formValues } as WarehouseShelvingUpdateInput)
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={onClose} />
  ) : (
    <Dialog data-testid='warehouse-shelving-form-dialog' open onClose={handleClose} fullWidth>
      <DialogHeader title='Edit Shelving' onClose={handleClose} />
      <DialogContent className='p-10'>
        <FormProvider {...methods}>
          <WarehouseShelvingForm shelving={shelving} />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button data-testid='cancel-button' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button data-testid='submit-button' variant='primary' disabled={!canSubmit} onClick={handleSubmit(submitForm)}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default WarehouseShelvingFormDialog
